import { CSSProperties } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useMaestroBrief } from '../../../lib/use-maestro-brief';
import { Button, Card } from '../ui';
import { AgentDot } from '../../AgentDot';
import { MaestroMark } from '../MaestroMark';
import { BriefHeadlinePositional } from './BriefHeadlinePositional';
import { BriefListenButton } from './BriefListenButton';
import { MOBILE_BRIEF_HEADLINE, MOBILE_BRIEF_HEADLINE_ACCENT } from './styles';

// Mobile-only rendering of the Morning Brief hero — same data as `MaestroBrief`
// (via `useMaestroBrief`), presented as a floating card per Figma 13517:97085: the
// Maestro mark + "Morning Brief, {time}", a divider, the agent cluster + "N Agents";
// the editorial headline (sans base, serif on the 2nd + 2nd-to-last word); and a
// "Read the meeting summary" button beside a speaker icon-button that plays the brief.

// The meta row's two label styles — Figma: label 14/20 SemiBold -0.42, count 12/14 Medium -0.6.
const META_LABEL: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 14,
  fontWeight: 'var(--sym-weight-semibold)',
  lineHeight: '20px',
  letterSpacing: '-0.42px',
  color: 'rgb(var(--ink-rgb))',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const META_COUNT: CSSProperties = {
  fontFamily: 'var(--brand-font-ui)',
  fontSize: 12,
  fontWeight: 'var(--sym-weight-medium)',
  lineHeight: '14px',
  letterSpacing: '-0.6px',
  color: 'rgb(var(--ink-rgb))',
  whiteSpace: 'nowrap',
};

export function MobileMaestroBrief() {
  const { t } = useTranslation();
  const brief = useMaestroBrief();

  if (!brief) return null;
  const { agentIds, agentCount, title, time, inProgress, headline, open } = brief;
  const baseLabel = title || t('brief.title', { time });
  const label = inProgress ? `${baseLabel} · ${t('brief.inProgress')}` : baseLabel;
  const heroHeadline = inProgress ? t('brief.inMotionHeadline') : headline;

  return (
    <Card style={{ textAlign: 'start' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <span style={{ display: 'inline-flex', flexShrink: 0 }}>
            <MaestroMark size={20} />
          </span>
          <span style={META_LABEL}>{label}</span>
        </span>
        {agentCount > 0 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span
              aria-hidden
              style={{ width: 1, height: 14, background: 'var(--ds-border-light-default)', flexShrink: 0 }}
            />
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              {agentIds.slice(0, 3).map((id, i) => (
                <span key={id} style={{ marginInlineStart: i === 0 ? 0 : -6, display: 'inline-flex' }}>
                  <AgentDot id={id} size={16} />
                </span>
              ))}
            </span>
            <span style={META_COUNT}>
              {agentCount} {t(agentCount === 1 ? 'island.agent' : 'island.agents')}
            </span>
          </span>
        )}
      </div>

      {heroHeadline && (
        <div style={{ ...MOBILE_BRIEF_HEADLINE, marginBottom: 16 }}>
          <BriefHeadlinePositional text={heroHeadline} accentStyle={MOBILE_BRIEF_HEADLINE_ACCENT} />
        </div>
      )}

      {/* Figma 13517-97120: a text-only primary "Read the meeting summary" button (no trailing icon)
          beside a bordered volume-2 icon button (the listen affordance). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button size="medium" onClick={open}>
          {t('brief.seeFullBrief')}
        </Button>
        <BriefListenButton iconOnly />
      </div>
    </Card>
  );
}
